import { useState, useEffect, useCallback } from 'react';
import { spotifyService, SpotifyTrack } from '@/services/spotifyService';
import { toast } from 'sonner';

export interface UseSpotifySearchResult {
  tracks: SpotifyTrack[];
  isLoading: boolean;
  error: string | null;
  searchTracks: (query: string) => void;
  clearResults: () => void;
}

export const useSpotifySearch = (debounceMs: number = 300): UseSpotifySearchResult => {
  const [tracks, setTracks] = useState<SpotifyTrack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Debounced search effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      setTracks([]);
      setError(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      setError(null);

      try {
        const results = await spotifyService.searchTracks(searchQuery, 5);
        setTracks(results);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to search Spotify';
        setError(errorMessage);
        setTracks([]);
        
        // Don't show toast for network errors that might be temporary
        if (!errorMessage.includes('Unable to authenticate')) {
          toast.error('Spotify search failed', {
            description: errorMessage
          });
        }
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, debounceMs]);

  const searchTracks = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const clearResults = useCallback(() => {
    setTracks([]);
    setSearchQuery('');
    setError(null);
  }, []);

  return {
    tracks,
    isLoading,
    error,
    searchTracks,
    clearResults,
  };
}; 